import { useMemo, type ReactNode } from "react";
import { sanitizeAdoHtml } from "@/lib/ado-html";
import { cn } from "@/lib/utils";

/**
 * Rendered Azure DevOps HTML, styled the way ADO's comment and description
 * views show it (UX-PM-31 preview, UX-PM-44; feature PI-SPEC-SW-1471).
 *
 * The app runs Tailwind v4 without the typography plugin, so the base styles
 * reset headings, list markers and link colours and `prose` classes do
 * nothing. This component restores them with descendant variants, in one
 * place, for every surface that shows tracker HTML:
 *
 * - `light`: the white ADO comment card (Post all "How it looks in ADO").
 *   Links in ADO blue `#0078D4`.
 * - `dark`: tracker HTML inside the dark app (Formulator review and diff).
 *
 * The input always goes through {@link sanitizeAdoHtml} here, so no caller
 * can render tracker HTML raw. When nothing visible is left, `empty` is
 * shown instead (nothing by default).
 */

export type AdoContentTone = "light" | "dark";

/** Headings, lists, links and spacing shared by both tones. Exported for tests. */
export const ADO_CONTENT_BASE = cn(
  "min-w-0 break-words",
  // Block spacing: paragraphs and blocks a little apart, none at the edges.
  "[&_p]:my-1.5 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
  // Headings: larger and bolder than body text, as ADO shows them.
  "[&_h1]:mb-1.5 [&_h1]:mt-3 [&_h1]:text-[1.45em] [&_h1]:font-semibold [&_h1]:leading-tight",
  "[&_h2]:mb-1.5 [&_h2]:mt-3 [&_h2]:text-[1.3em] [&_h2]:font-semibold [&_h2]:leading-tight",
  "[&_h3]:mb-1 [&_h3]:mt-2.5 [&_h3]:text-[1.12em] [&_h3]:font-semibold [&_h3]:leading-snug",
  "[&_h4]:mb-1 [&_h4]:mt-2 [&_h4]:font-semibold [&_h5]:mt-2 [&_h5]:font-semibold [&_h6]:mt-2 [&_h6]:font-semibold",
  // Lists: numbered and bulleted again, indented, nested bullets as circles.
  "[&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-6",
  "[&_ul_ul]:list-[circle] [&_li]:my-0.5 [&_li]:pl-0.5 [&_li>p]:my-0",
  // Inline formatting.
  "[&_b]:font-semibold [&_strong]:font-semibold [&_em]:italic [&_i]:italic [&_u]:underline [&_s]:line-through [&_strike]:line-through",
  "[&_a]:underline-offset-2 [&_a:hover]:underline",
  "[&_code]:rounded [&_code]:px-1 [&_code]:font-mono [&_code]:text-[0.92em]",
  "[&_pre]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_pre]:rounded [&_pre]:p-2 [&_pre]:font-mono [&_pre]:text-[0.92em]",
  "[&_blockquote]:my-1.5 [&_blockquote]:border-l-[3px] [&_blockquote]:pl-3",
  "[&_hr]:my-3 [&_table]:my-1.5 [&_table]:border-collapse [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold"
);

/** Colours per tone. Exported for tests. */
export const ADO_CONTENT_TONE: Record<AdoContentTone, string> = {
  light: cn(
    "text-[#201F1E] [&_h1]:text-[#201F1E] [&_h2]:text-[#201F1E] [&_h3]:text-[#201F1E]",
    "[&_a]:text-[#0078D4] [&_li]:marker:text-[#605E5C]",
    "[&_code]:bg-[#F3F2F1] [&_pre]:bg-[#F3F2F1] [&_blockquote]:border-[#C8C6C4] [&_blockquote]:text-[#605E5C]",
    "[&_hr]:border-[#EDEBE9] [&_td]:border-[#C8C6C4] [&_th]:border-[#C8C6C4]"
  ),
  dark: cn(
    "[&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_h4]:text-foreground",
    "[&_a]:text-link [&_li]:marker:text-muted-foreground",
    "[&_code]:bg-white/[0.06] [&_pre]:bg-white/[0.04] [&_blockquote]:border-white/15 [&_blockquote]:text-muted-foreground",
    "[&_hr]:border-white/10 [&_td]:border-white/10 [&_th]:border-white/10"
  ),
};

export interface AdoContentProps {
  /** Tracker HTML as ADO stores it. Sanitised here; never rendered raw. */
  html: string;
  tone: AdoContentTone;
  className?: string;
  testId?: string;
  /** Shown when nothing visible is left after sanitising. Nothing by default. */
  empty?: ReactNode;
}

export function AdoContent({ html, tone, className, testId, empty = null }: AdoContentProps) {
  const clean = useMemo(() => (html.trim() === "" ? null : sanitizeAdoHtml(html)), [html]);
  if (clean === null) return <>{empty}</>;
  return (
    <div
      className={cn(ADO_CONTENT_BASE, ADO_CONTENT_TONE[tone], className)}
      data-testid={testId}
      data-ado-content={tone}
      // Sanitised by sanitizeAdoHtml above; never rendered raw.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
